import { create } from '@bufbuild/protobuf';
import {
  MountPointSchema,
  MountPointType,
  type MountPoint,
} from '@/proto/agentplatform/v1/sandbox_pb';
import { GenericServiceSecretDataSchema } from '@/proto/agentplatform/v2/secret_pb';
import type { StorageTypeCase } from '@/lib/secretStorage';
import { secretApi } from '@/api/secret.api';
import { ApiError } from '@/api/errors';

export const MAX_MOUNTS = 10;

/**
 * Builds a user-facing message for a sandbox/template save failure. httpv2 errors
 * come back with HTTP 200 and the real cause in the envelope's data.message
 * (ApiError.detail), e.g. object storage credential rejection; prefer that over
 * the generic status text so the mount validation reason is actually visible.
 */
export function saveErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return `保存失败：${error.detail || error.message}`;
  }
  if (error instanceof Error) {
    return `保存失败：${error.message}`;
  }
  return '保存失败，请稍后重试';
}

export interface InlineCredentialDraft {
  name: string;
  storageType: StorageTypeCase;
  region: string;
  endpoint: string;
  accessKey: string;
  secretKey: string;
}

export function emptyInlineCredential(): InlineCredentialDraft {
  return {
    name: '',
    storageType: 'aliyunOss',
    region: '',
    endpoint: '',
    accessKey: '',
    secretKey: '',
  };
}

export type CredentialSource = 'existing' | 'inline';

export interface MountDraft {
  secretId: string;
  credentialSource: CredentialSource;
  inlineCredential: InlineCredentialDraft;
  bucket: string;
  bucketPath: string;
  mountPath: string;
  readOnly: boolean;
  remark: string;
}

export function emptyMount(): MountDraft {
  return {
    secretId: '',
    credentialSource: 'existing',
    inlineCredential: emptyInlineCredential(),
    bucket: '',
    bucketPath: '/',
    mountPath: '',
    readOnly: true,
    remark: '',
  };
}

export function normalizeBucketPath(value: string): string {
  const v = value.trim();
  if (!v) return '/';
  const normalized = v.startsWith('/') ? v : `/${v}`;
  return normalized.length > 1 ? normalized.replace(/\/+$/, '') : normalized;
}

export function normalizeMountPath(value: string): string {
  let v = value.trim();
  if (!v) return v;
  if (!v.startsWith('/')) v = `/${v}`;
  if (v.length > 1) v = v.replace(/\/+$/, '');
  return v || '/';
}

/**
 * Converts drafts to MountPoint list. Each draft carries its own resolved secretId.
 * Inline credentials must be created beforehand; pass the resulting secretId in draft.secretId.
 */
export function toMountPoints(mounts: MountDraft[]): MountPoint[] {
  return mounts.map((m) =>
    create(MountPointSchema, {
      type: MountPointType.OBJECT_STORAGE,
      secretId: m.secretId,
      source: m.bucket.trim(),
      subDir: normalizeBucketPath(m.bucketPath),
      mountPath: normalizeMountPath(m.mountPath),
      readOnly: m.readOnly,
      note: m.remark.trim(),
    })
  );
}

export function fromMountPoints(mountPoints: MountPoint[]): MountDraft[] {
  const objectStore = mountPoints.filter((m) => m.type === MountPointType.OBJECT_STORAGE);
  if (objectStore.length === 0) return [emptyMount()];
  return objectStore.map((m) => ({
    secretId: m.secretId,
    credentialSource: 'existing' as CredentialSource,
    inlineCredential: emptyInlineCredential(),
    bucket: m.source,
    bucketPath: m.subDir || '/',
    mountPath: m.mountPath,
    readOnly: m.readOnly,
    remark: m.options?.remark ?? m.note ?? '',
  }));
}

const FORBIDDEN_MOUNT_PATHS = new Set([
  '/',
  '/tmp',
  '/skills',
  '/bin',
  '/boot',
  '/dev',
  '/etc',
  '/lib',
  '/lib64',
  '/proc',
  '/root',
  '/run',
  '/sbin',
  '/sys',
  '/usr',
  '/var',
]);

function isAncestorOrEqual(a: string, b: string): boolean {
  if (a === b) return true;
  const prefix = a.endsWith('/') ? a : `${a}/`;
  return b.startsWith(prefix);
}

function validateBucketName(bucket: string): string | null {
  const b = bucket.trim();
  if (b.length < 3 || b.length > 128) return 'Bucket 名称长度须在 3-128 个字符之间';
  if (b.includes('/')) return 'Bucket 名称不能包含 /';
  if (/^https?:\/\//i.test(b)) return 'Bucket 名称不能是 URL';
  return null;
}

function validateBucketSubdir(subdir: string): string | null {
  const s = subdir.trim();
  if (s.includes('..')) return 'Bucket 子目录不能包含 ..';
  if (/\/\//.test(s)) return 'Bucket 子目录不能包含连续斜杠';
  return null;
}

export interface StorageMountValidationError {
  reason: string;
}

export function validateStorageMounts(mounts: MountDraft[]): StorageMountValidationError | null {
  const first = mounts[0];
  if (first && first.credentialSource === 'existing' && !first.secretId) {
    return { reason: '请选择或新建一个对象存储凭证' };
  }

  const seenPaths: string[] = [];
  for (let i = 0; i < mounts.length; i++) {
    const m = mounts[i];
    const label = `挂载点 ${i + 1}`;

    const bucketErr = validateBucketName(m.bucket);
    if (bucketErr) return { reason: `${label}：${bucketErr}` };

    const subdirErr = validateBucketSubdir(m.bucketPath);
    if (subdirErr) return { reason: `${label}：${subdirErr}` };
    if (!m.bucketPath.trim()) return { reason: `${label}：Bucket 子目录必填` };

    const mountPath = normalizeMountPath(m.mountPath);
    if (!mountPath) return { reason: `${label}：本地目录必填` };
    if (FORBIDDEN_MOUNT_PATHS.has(mountPath)) {
      return { reason: `${label}：本地目录 ${mountPath} 是系统或平台保留路径，不能用于挂载` };
    }

    for (const prev of seenPaths) {
      if (isAncestorOrEqual(prev, mountPath) || isAncestorOrEqual(mountPath, prev)) {
        return { reason: `${label}：本地目录 ${mountPath} 与已有挂载点 ${prev} 存在父子路径冲突` };
      }
    }
    seenPaths.push(mountPath);
  }
  return null;
}

/**
 * 保存沙箱/模版前的联网凭证预校验。存储区是全局单一凭证 + 多 bucket：取所有 mount
 * 的 bucket 组成待验证列表，existing 凭证按 secretId 校验，inline 凭证按明文数据校验。
 * 失败时抛出 ApiError（由调用方转成 storageError 展示），成功则静默返回。
 */
export async function validateMountCredential(mounts: MountDraft[]): Promise<void> {
  if (mounts.length === 0) return;
  const buckets = mounts.map((m) => m.bucket.trim()).filter(Boolean);
  const first = mounts[0];
  if (first.credentialSource === 'existing') {
    await secretApi.validateOSSSecret({ buckets, secretId: first.secretId });
    return;
  }
  const c = first.inlineCredential;
  await secretApi.validateOSSSecret({
    buckets,
    storageType: c.storageType,
    body: create(GenericServiceSecretDataSchema, {
      endpoint: c.endpoint.trim(),
      region: c.region.trim(),
      accessKey: c.accessKey.trim(),
      secretKey: c.secretKey,
    }),
  });
}
